import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import * as _ from 'lodash';

import {
  AppointmentIds,
  DeleteTrialUsersReportsPayloadType,
  GetReportWritebackFailureAnalysisPayloadType,
  ReportDataType
} from './interfaces/index';
import {
  ACTION_NAMES,
  DATE_FORMATS,
  DB_REPORTS_STATUS_MAPPING,
  SLICES
} from './../../constants/index';
import {
  getReportsByFilters,
  getReportWritebackAnalysis,
  hardDeleteReportsByAppoitmentIds
} from '../services/report/api';
import { createResetAction, formatDate, sortSearchedData } from '../../utils';
import axiosInstance from '../../axiosInstance';
import { RootState, StoreMethods } from '../store';
import {
  GetReportsByFilterPayloadType,
  GetReportsDataType
} from './interfaces';
import { PaginationParams } from '../user/interfaces';
import { setShowAlert } from '../utilts/utilsSlice';
import { TOAST_TYPE } from './../../utils/constantTypes';

type FetchReportsTranscriptPayloadType = {
  accessToken: string;
  appointmentId: string;
};

type FetchReportDetailsPayloadType = FetchReportsTranscriptPayloadType;

interface Report {
  id: string;
  createdBy: string;
  updatedBy: string | null;
  appointmentId: string;
  patientId: string;
  noteId: string | null;
  sikkaPracticeId: string | null;
  providerId: string | null;
  description: string;
  lastFileIndex: number | null;
  procedureCodes: string[];
  procedureKeys: string[];
  fileName: string | null;
  status: string;
  reportUrl: string | null;
  signature: {
    sign: string;
    signFont: string;
    name: string;
    title: string;
    license: string;
  } | null;
  createdAt: string;
  updatedAt: string;
}

interface ReportState {
  loading: boolean;
  error: null | string;
  data: [];
  totalCount: number;
  reportDetails: {};
  searchedReportsStats: {
    total: number;
    submitted: number;
    complete: number;
    paused: number;
    start: number;
    totalReportCost?: number;
  };
  searchedReportsWritebacksStats: {
    total: number;
    success: number;
    pending: number;
    failed: number;
  };
  transcript: string;
  searchedReports: ReportDataType[];
  offset: number;
  limit: number;
  sortBy: string;
  sortDirection: 'asc' | 'desc';
  selectedReport: ReportDataType | null;
}
const initialState: ReportState = {
  loading: false,
  error: null,
  data: [],
  totalCount: 0,
  reportDetails: {},
  searchedReportsStats: {
    total: 0,
    submitted: 0,
    complete: 0,
    paused: 0,
    start: 0,
    totalReportCost: 0
  },
  searchedReportsWritebacksStats: {
    total: 0,
    success: 0,
    pending: 0,
    failed: 0
  },
  transcript: '',
  searchedReports: [],
  offset: 0,
  limit: 10,
  sortBy: '',
  sortDirection: 'asc',
  selectedReport: null
};

const reportSlice = createSlice({
  name: 'report',
  initialState,
  reducers: {
    setSortBy: (state, action) => {
      state.sortBy = action.payload;
    },
    setSortDirection: (state, action) => {
      state.sortDirection = action.payload;
    },
    setPagination: (state, action: PayloadAction<PaginationParams>) => {
      state.limit = action.payload.limit;
      state.offset = action.payload.offset;
    },
    setSearchedReports: (state, action: PayloadAction<string>) => {
      const sortedData = sortSearchedData(
        state.searchedReports,
        action.payload,
        state.sortDirection
      );
      state.searchedReports = sortedData;
    },
    resetSearchedReports: (state) => {
      state.searchedReports = [];
    },
    setSearchedReportsStats: (
      state,
      action: PayloadAction<ReportState['searchedReportsStats']>
    ) => {
      state.searchedReportsStats = action.payload;
    },
    setSearchedReportsWritebackStats: (
      state,
      action: PayloadAction<ReportState['searchedReportsWritebacksStats']>
    ) => {
      state.searchedReportsWritebacksStats = action.payload;
    },
    setSelectedReport: (
      state,
      action: PayloadAction<ReportDataType | null>
    ) => {
      state.selectedReport = action.payload;
    },
    resetReport: createResetAction(initialState)
  },
  extraReducers: (builder) => {
    builder.addCase(fetchReportsAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchReportsAsync.fulfilled, (state, action) => {
      state.loading = false;
      state.totalCount = action?.payload?.totalCount;
      state.searchedReports = action?.payload?.data;
      state.searchedReportsStats = action?.payload?.stats;
      state.searchedReportsWritebacksStats = action?.payload?.writebackStats;
    });
    builder.addCase(fetchReportsAsync.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(fetchReportsTranscriptAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchReportsTranscriptAsync.fulfilled, (state, action) => {
      state.loading = false;
      state.transcript = action.payload.transcriptData;
    });
    builder.addCase(fetchReportsTranscriptAsync.rejected, (state, action) => {
      const msg = _.get(action, 'payload.msg');
      state.loading = false;
      state.error = msg;
    });
    builder.addCase(fecthReportByIdAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fecthReportByIdAsync.fulfilled, (state, action) => {
      state.loading = false;
      const reportDetails = _.omit(action.payload.reportData, [
        'msg',
        'statusCode'
      ]);
      state.reportDetails = reportDetails;
    });
    builder.addCase(fecthReportByIdAsync.rejected, (state, action) => {
      const msg = _.get(action, 'payload.msg');
      state.loading = false;
      state.error = msg;
    });
    builder.addCase(getReportWritebackFailureAnalysisAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getReportWritebackFailureAnalysisAsync.fulfilled,
      (state, action) => {
        state.loading = false;
        const reportWritebackFailureAnalysis = _.get(action, 'payload', {});
        state.selectedReport = {
          ...state.selectedReport,
          writebackAnalysis: {
            ...state.selectedReport.writebackAnalysis,
            ...reportWritebackFailureAnalysis
          }
        };
      }
    );
    builder.addCase(
      getReportWritebackFailureAnalysisAsync.rejected,
      (state, action) => {
        state.loading = false;
      }
    );
  }
});

export const fetchReportsAsync = createAsyncThunk<
  GetReportsDataType,
  GetReportsByFilterPayloadType,
  StoreMethods
>(
  `${SLICES.REPORT}/${ACTION_NAMES.GET_REPORTS_BY_FILTERS}`,
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const reportRes = await getReportsByFilters(payload, { dispatch });
      const reportData = _.get(reportRes, 'data.data', []);
      const searchedReportsStats = _.get(reportRes, 'data.stats', {});
      const searchedReportsWritebackStats = _.get(
        reportRes,
        'data.writebackStats',
        {}
      );
      const totalCount = _.get(reportRes, 'data.totalCount', 0);

      const modifiedReports = reportData?.map((report) => ({
        id: report?._id,
        appointmentId: report?.appointmentId,
        patientId: report?.patientId,
        patientEmail: report?.patientEmail,
        reportUrl: report?.reportUrl,
        description: report?.description,
        procedureCodes: report?.procedureCodes,
        procedureKeys: report?.procedureKeys,
        patientName: report?.patientName,
        appointmentTime: report?.appointmentTime,
        appointmentDate: formatDate({
          date: report?.appointmentDate,
          format: DATE_FORMATS.MONTH_FIRST
        }),
        appointmentLength: isNaN(Number(report?.appointmentLength))
          ? report?.appointmentLength
          : Number(report?.appointmentLength),
        operatory: report?.operatory,
        status: DB_REPORTS_STATUS_MAPPING[report?.status],
        createdAt: formatDate({
          date: report?.createdAt,
          format: DATE_FORMATS.MONTH_FIRST
        }),
        updatedAt: formatDate({
          date: report?.updatedAt,
          format: DATE_FORMATS.MONTH_FIRST
        }),
        userName: report?.userName,
        writebackStatus: report?.writebackStatus,
        providerId: report?.providerId,
        writebackIds: report?.writebackIds,
        pausedAt: report?.pausedAt,
        pausedTime: report?.pausedTime,
        stoppedAt: report?.stoppedAt,
        reportProcessingAt: report?.reportProcessingAt,
        initialNotificationSentAt: report?.initialNotificationSentAt,
        reportCompletedAt: report?.reportCompletedAt,
        finalNotificationSentAt: report?.finalNotificationSentAt,
        submittedAt: report?.submittedAt,
        submittedForWritebackAt: report?.submittedForWritebackAt,
        writebackSuccessAt: report?.writebackSuccessAt,
        tokens: {
          totalReportCost: report?.tokens?.soapReportTotalPrice ?? null,
          totalAnalysisCost: report?.tokens?.analysisTotalPrice ?? null,
          totalReportSumaryCost:
            report?.tokens?.reportSummaryTotalPrice ?? null,
          totalSpecialistReportCost:
            report?.tokens?.specialistReportTotalPrice ?? null,
          totalAfterCareSummaryReportCost:
            report?.tokens?.afterCareSummaryReportTotalPrice ?? null,
          totalFinancialNotesCost: report?.tokens?.tcNotesTotalPrice ?? null
        },
        perioChart: report?.perioChart ?? null
      }));
      return {
        totalCount,
        data: sortSearchedData(modifiedReports, 'createdAt', 'desc'),
        stats: searchedReportsStats,
        writebackStats: searchedReportsWritebackStats
      };
    } catch (err) {
      console.log('Error', err);
      return rejectWithValue(err?.message);
    }
  }
);

export const fecthReportByIdAsync = createAsyncThunk(
  'report/fetchReportById',
  async (payload: FetchReportDetailsPayloadType, { rejectWithValue }) => {
    try {
      const requestId = _.get(payload, 'appointmentId');
      const accessToken = _.get(payload, 'accessToken');
      const reportRes = await axiosInstance.post(
        'report/reportById',
        {
          requestId
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        }
      );
      const reportData = _.get(reportRes, 'data.data', '');
      return { reportData };
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  }
);

export const fetchReportsTranscriptAsync = createAsyncThunk(
  'report/fetchReportsTranscript',
  async (payload: FetchReportsTranscriptPayloadType, { rejectWithValue }) => {
    try {
      const requestId = _.get(payload, 'appointmentId');
      const accessToken = _.get(payload, 'accessToken');
      const transcriptRes = await axiosInstance.post(
        'readTranscript',
        {
          requestId: '79709'
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        }
      );
      const transcriptData = _.get(transcriptRes, 'data.data', '');
      return { transcriptData };
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  }
);

export const deleteTrialUsersReportsAsync = createAsyncThunk<
  AppointmentIds,
  DeleteTrialUsersReportsPayloadType,
  StoreMethods
>(
  `${SLICES.REPORT}/${ACTION_NAMES.DELETE_TRIAL_USERS_REPORTS}`,
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const res = await hardDeleteReportsByAppoitmentIds(payload, { dispatch });
      const statusCode = _.get(res, 'statusCode', null);
      if (statusCode === 200) {
        dispatch(
          setShowAlert({
            show: true,
            message: _.get(res, 'msg'),
            type: TOAST_TYPE.SUCCESS
          })
        );
        return payload?.data;
      }
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  }
);

export const getReportWritebackFailureAnalysisAsync = createAsyncThunk<
  any,
  GetReportWritebackFailureAnalysisPayloadType,
  StoreMethods
>(
  `${SLICES.REPORT}/${ACTION_NAMES.GET_REPORT_WRITEBACK_FAILURE_ANALYSIS}`,
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const res = await getReportWritebackAnalysis(payload, { dispatch });
      const writebackAnalysis = _.get(res, 'data.writebackAnalysis', {});
      return writebackAnalysis;
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  }
);

export const {
  setSortBy,
  setSortDirection,
  setPagination,
  setSearchedReports,
  resetReport,
  resetSearchedReports,
  setSearchedReportsStats,
  setSearchedReportsWritebackStats,
  setSelectedReport
} = reportSlice.actions;

export const getReports = (state: RootState) => state?.report?.searchedReports;
export const getSearchedReportsStats = (state: RootState) =>
  state?.report?.searchedReportsStats;
export const getSearchedReportsWritebackStats = (state: RootState) =>
  state?.report?.searchedReportsWritebacksStats;
export const getSelectedReport = (state: RootState) =>
  state?.report?.selectedReport;
export default reportSlice.reducer;
